import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Grid, Image, Text } from "@chakra-ui/react";
import {
  TrendBrandsItem,
  TrendingBrandsQuery,
} from "@/features/dashboard/features/trending-brand-section/types";
import { useGetTrendingBrandList } from "@/features/dashboard/features/trending-brand-section/hooks/useGetTrendingBrandList";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useSearchParams } from "react-router-dom";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";

interface Props {
  query: TrendingBrandsQuery;
}

function TrendingBrandsGrid({ query }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<TrendBrandsItem[]>([]);
  const [extraBrands, setExtraBrands] = useState<TrendBrandsItem[]>([]);
  const brandList = useGetCompanies(["BRAND"]);

  useEffect(() => {
    const ids = searchParams.getAll("brand_ids").map((id) => Number(id));
    setBrandIds(ids);
  }, [searchParams]);

  const params = useMemo(
    () => ({
      by: query.by ?? "view",
      period: query.period ?? "7 days",
      buyer_id: query.buyer_id
    }),
    [query]
  );
  const { trendingBrandList } = useGetTrendingBrandList(params);
  const { navigateToLineSheetPage } = useRouterHelper();
  const { tTitle } = useI18nHelper();

  useEffect(() => {
    let filtered: TrendBrandsItem[] = [];
    let extra: TrendBrandsItem[] = [];
    if (brandIds && brandIds.length > 0) {
      filtered = brandIds.map((id) => {
        if (trendingBrandList.find((brand: { brand_id: number; }) => brand.brand_id === id)) {
          return trendingBrandList.find((brand: { brand_id: number; }) => brand.brand_id === id)!;
        } else {
          const brandInfo = brandList?.find((brand) => brand.id === id);
          return {
            brand_id: id,
            name: brandInfo?.name || "Unknown Brand",
            image_url: brandInfo?.logoUrl || "",
            ranking: '-',
            in_valid_line_sheet: false,
          };
        }
      }).sort((a, b) => {
        if (a.ranking === '-' && b.ranking === '-') return 0;
        if (a.ranking === '-') return 1;
        if (b.ranking === '-') return -1;
        return a.ranking - b.ranking;
      });
      extra = trendingBrandList.filter(
        (brand: { brand_id: number }) => !brandIds.includes(brand.brand_id)
      );
    } else {
      filtered = trendingBrandList;
    }
    setFilteredBrands(filtered);
    setExtraBrands(extra);
  }, [brandIds, brandList, trendingBrandList]);

  return (
    <>
      {
        filteredBrands.length > 0 && <Grid templateColumns={"1fr 1fr"} gap={"12px"} padding={"0 4px 8px 16px"}>
        {filteredBrands.map((brand: TrendBrandsItem) => (
          <Flex
            key={brand.name + brand.ranking}
            border={"1px solid #D4D4D1"}
            borderRadius={"4px"}
            padding={"8px 4px"}
            alignItems={"center"}
            gap={"6px"}
            _hover={{
              borderColor: brand.in_valid_line_sheet ? "#1272EF" : "#D4D4D1"
            }}
            position={"relative"}
            cursor={brand.in_valid_line_sheet ? "pointer" : "default"}
            className={"group"}
          >
            <Text
              textStyle={"body-md-bold"}
              width={"28px"}
              textAlign={"center"}
              color={brandIds.length > 0 ? "#1272EF" : "#444444"}
            >
              {brand.ranking}
            </Text>
            <Image
              src={brand.image_url}
              alt={brand.name}
              width={"36px"}
              height={"36px"}
              fallbackSrc={"/icons-v1_1/null.png"}
              borderRadius={"full"}
            />
            <Text textStyle={"body-md-medium"} color={brandIds.length > 0 ? "#1272EF" : "#444444"}>{brand.name}</Text>
            <Box>
              <Button
                background={"#FCFCFB"}
                border={"1px solid #8F8F8C"}
                padding={"4px 8px"}
                height={"28px"}
                position={"absolute"}
                display={"none"}
                top={"50%"}
                right={"12px"}
                onClick={() => {
                  navigateToLineSheetPage(undefined, brand.brand_id);
                }}
                transform={"translateY(-50%)"}
                _hover={{
                  borderColor: "#1272EF",
                  "> *": { color: "#1272EF" }
                }}
                _groupHover={{
                  display: brand.in_valid_line_sheet ? "inline-block" : "none"
                }}
              >
                <Text textStyle={"body-sm-bold"} color={"#444444"}>
                  {tTitle("common\:navigation.stock")}
                </Text>
              </Button>
            </Box>
          </Flex>
        ))}
      </Grid>}
      {
        brandIds.length > 0 && (
          <Grid templateColumns={"1fr 1fr"} gap={"12px"} margin={"0 4px 16px 16px"} paddingTop={"8px"} borderTop={"0.5px solid #8F8F8C"}>
            {
              extraBrands.map((brand: TrendBrandsItem) => (
                <Flex
                  key={brand.name + brand.ranking}
                  border={"1px solid #D4D4D1"}
                  borderRadius={"4px"}
                  padding={"8px 4px"}
                  alignItems={"center"}
                  gap={"6px"}
                  _hover={{
                    borderColor: brand.in_valid_line_sheet ? "#1272EF" : "#D4D4D1",
                  }}
                  position={"relative"}
                  cursor={brand.in_valid_line_sheet ? "pointer" : "default"}
                  className={"group"}
                >
                  <Text
                    textStyle={"body-md-bold"}
                    width={"28px"}
                    textAlign={"center"}
                  >
                    {brand.ranking}
                  </Text>
                  <Image
                    src={brand.image_url}
                    alt={brand.name}
                    width={"36px"}
                    height={"36px"}
                    fallbackSrc={"/icons-v1_1/null.png"}
                    borderRadius={"full"}
                  />
                  <Text textStyle={"body-md-medium"}>{brand.name}</Text>
                  <Box>
                    <Button
                      background={"#FCFCFB"}
                      border={"1px solid #8F8F8C"}
                      padding={"4px 8px"}
                      height={"28px"}
                      position={"absolute"}
                      display={"none"}
                      top={"50%"}
                      right={"12px"}
                      onClick={() => {
                        navigateToLineSheetPage(undefined, brand.brand_id);
                      }}
                      transform={"translateY(-50%)"}
                      _hover={{
                        borderColor: "#1272EF",
                        "> *": { color: "#1272EF" },
                      }}
                      _groupHover={{
                        display: brand.in_valid_line_sheet ? "inline-block" : "none",
                      }}
                    >
                      <Text textStyle={"body-sm-bold"} color={"#444444"}>
                        {tTitle("common\:navigation.stock")}
                      </Text>
                    </Button>
                  </Box>
                </Flex>
              ))
            }
          </Grid>
        )
      }
    </>
  );
}

export default TrendingBrandsGrid;
